// Plans tab — list + create + edit + delete subscription plans. Plans drive
// the customer-facing pricing pages (klonit-dashboard PlanSelectionPage /
// PricingPage / Resubscribe). Editing a plan immediately affects pricing for
// every NEW subscriber on it; existing subscribers keep their current
// subscription.interval + feature cap until the next sync.
//
// Yearly billing lives on subscriptions.interval (Phase A), not on a separate
// plan row -- there's one row per (plan_tier, account_type) here, and the
// customer toggle on the dashboard side picks the cycle.
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, TextField, MenuItem, Table, TableHead, TableRow, TableCell,
    TableBody, TableContainer, Paper, Pagination, Chip, Switch, Alert,
    CircularProgress, Stack, IconButton, Tooltip, Button, Snackbar, Dialog,
    DialogTitle, DialogContent, DialogActions, Divider, Grid,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddIcon from '@mui/icons-material/Add';

import axiosInstance from '../utils/axiosInstance';
import { formatCurrency } from '../utils/format';
import { useDebounce } from '../utils/useDebounce';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const PAGE_SIZE = 50;

const ACCOUNT_TYPES = [
    { value: '', label: 'Any (not set)' },
    { value: 'business', label: 'Business' },
    { value: 'individual', label: 'Individual' },
];

const INTERVALS = [
    { value: '', label: 'None (free / one-time)' },
    { value: 'month', label: 'Month' },
    { value: 'year', label: 'Year' },
    { value: 'week', label: 'Week' },
    { value: 'day', label: 'Day' },
];

const DISCOUNT_TYPES = [
    { value: 'percentage', label: 'Percentage (%)' },
    { value: 'fixed', label: 'Fixed amount' },
];

// Numeric feature limits. -1 = unlimited (existing convention).
const NUMERIC_LIMITS = [
    { key: 'web_pages_limit', label: 'Web pages' },
    { key: 'document_limit', label: 'Documents' },
    { key: 'document_size_limit_mb', label: 'Document size (MB)' },
    { key: 'image_limit', label: 'Images' },
    { key: 'image_size_limit_mb', label: 'Image size (MB)' },
    { key: 'audio_limit', label: 'Audio files' },
    { key: 'audio_time_limit', label: 'Audio time (min)' },
    { key: 'llm_tokens_in_limit', label: 'LLM tokens in' },
    { key: 'llm_tokens_out_limit', label: 'LLM tokens out' },
    { key: 'max_chatbot_limit', label: 'Max chatbots' },
];

const BOOL_FEATURES = [
    { key: 'multilingual', label: 'Multilingual' },
    { key: 'google_calendar', label: 'Google Calendar' },
    { key: 'ecommerce', label: 'E-commerce' },
    { key: 'ai_agents', label: 'AI Agents' },
    { key: 'google_drive', label: 'Google Drive' },
    { key: 'sql_database', label: 'SQL Database' },
    { key: 'reports', label: 'Reports' },
    { key: 'avatars', label: 'Avatars' },
    { key: 'monetizes', label: 'Monetizes' },
];

const EMPTY_PLAN = {
    name: '', display_name: '', price: 0, currency: 'usd',
    interval: 'month', account_type: 'business',
    is_public: true, is_trial_available: false, trial_days: 0,
    discount_type: 'percentage', discount_value: 0,
    web_pages_limit: null, document_limit: null, document_size_limit_mb: null,
    image_limit: null, image_size_limit_mb: null, audio_limit: null,
    audio_time_limit: null, llm_tokens_in_limit: null, llm_tokens_out_limit: null,
    max_chatbot_limit: null, allowed_llms: '',
    multilingual: false, google_calendar: false, ecommerce: false, ai_agents: false,
    google_drive: false, sql_database: false, reports: true, avatars: true,
    monetizes: true,
    whatsapp_retrieval_price: null, whatsapp_outbound_price: null,
    sms_outbound_price: null,
};

// `null` ↔ '' for numeric inputs. The backend treats blank as null.
const toNumOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

const Plans = () => {
    const [plans, setPlans] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(1);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [accountType, setAccountType] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState(null);

    const [editing, setEditing] = useState(null);            // plan object OR EMPTY_PLAN
    const [busy, setBusy] = useState(false);

    const debouncedSearch = useDebounce(search, 350);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, limit: PAGE_SIZE };
            if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
            if (accountType) params.account_type = accountType;
            const res = await axiosInstance.get('/admin/plans', { params });
            setPlans(res.data?.plans || []);
            setTotal(res.data?.total || 0);
            setPages(res.data?.pages || 1);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load plans.');
        } finally {
            setLoading(false);
        }
    }, [page, debouncedSearch, accountType]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { document.title = 'Plans | Klonit Super Admin'; }, []);

    const handleSave = async () => {
        if (busy || !editing) return;
        setBusy(true);
        // Strip blanks for numeric fields so the backend stores NULL.
        const body = { ...editing };
        NUMERIC_LIMITS.forEach(({ key }) => { body[key] = toNumOrNull(body[key]); });
        ['whatsapp_retrieval_price', 'whatsapp_outbound_price', 'sms_outbound_price', 'trial_days', 'discount_value']
            .forEach(key => { body[key] = toNumOrNull(body[key]); });
        body.price = Number(body.price) || 0;
        try {
            if (editing.id) {
                // PATCH never lets the unique slug be renamed (immutable on the
                // server) so omit it from the body to avoid confusion.
                const { id, name, ...patch } = body;
                await axiosInstance.patch(`/admin/plans/${id}`, patch);
                setSnackbar({ severity: 'success', message: 'Plan updated.' });
            } else {
                await axiosInstance.post('/admin/plans', body);
                setSnackbar({ severity: 'success', message: 'Plan created.' });
            }
            setEditing(null);
            load();
        } catch (err) {
            setSnackbar({
                severity: 'error',
                message: err.response?.data?.message || 'Save failed.',
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box sx={{ fontFamily }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
                   alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
                <Box>
                    <Typography sx={{ fontFamily, fontSize: '1.4rem', fontWeight: 800, color: '#0f172a' }}>
                        Plans
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                        Pricing, feature limits, and per-plan discounts. Yearly billing is a per-subscription flag,
                        not a separate plan row.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8' }}>
                        {total} total
                    </Typography>
                    <Button
                        variant="contained" startIcon={<AddIcon />} size="small"
                        onClick={() => setEditing({ ...EMPTY_PLAN })}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 700 }}
                    >
                        New plan
                    </Button>
                </Stack>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                <TextField
                    size="small" label="Search name or display name" value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    sx={{ minWidth: 260 }}
                />
                <TextField
                    select size="small" label="Account type" value={accountType}
                    onChange={(e) => { setAccountType(e.target.value); setPage(1); }}
                    sx={{ minWidth: 180 }}
                >
                    {ACCOUNT_TYPES.map((t) => (
                        <MenuItem key={t.value || 'any'} value={t.value}>{t.label}</MenuItem>
                    ))}
                </TextField>
            </Stack>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error" sx={{ fontFamily }}>{error}</Alert>
            ) : plans.length === 0 ? (
                <Typography sx={{ fontFamily, color: '#64748b', py: 6, textAlign: 'center' }}>
                    No plans match the filters.
                </Typography>
            ) : (
                <>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px' }}>
                        <Table aria-label="plans">
                            <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                                <TableRow>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Name</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Display</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Account</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }} align="right">Price</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Interval</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Trial</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Discount</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }} align="center">Public</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }} align="center">Subs</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {plans.map((p) => (
                                    <TableRow key={p.id} hover>
                                        <TableCell sx={{ fontFamily, fontWeight: 600 }}>{p.name}</TableCell>
                                        <TableCell sx={{ fontFamily }}>{p.display_name}</TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b' }}>
                                            {p.account_type || '--'}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily }} align="right">
                                            {p.price > 0 ? formatCurrency(p.price, p.currency) : 'Free'}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b' }}>
                                            {p.interval || '--'}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b' }}>
                                            {p.is_trial_available ? `${p.trial_days}d` : '--'}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b' }}>
                                            {p.discount_value > 0
                                                ? `${p.discount_value}${p.discount_type === 'percentage' ? '%' : ''}`
                                                : '--'}
                                        </TableCell>
                                        <TableCell align="center">
                                            {p.is_public
                                                ? <Chip size="small" color="success" label="Public" sx={{ fontFamily }} />
                                                : <Chip size="small" label="Hidden" sx={{ fontFamily }} />}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily }} align="center">
                                            {p.subscriber_count ?? 0}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => setEditing({ ...p })}>
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {pages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Pagination
                                count={pages} page={page}
                                onChange={(_, p) => setPage(p)}
                                color="primary" size="small"
                            />
                        </Box>
                    )}
                </>
            )}

            {/* Create / edit dialog */}
            <PlanFormDialog
                open={!!editing} plan={editing}
                busy={busy}
                onChange={setEditing}
                onCancel={() => setEditing(null)}
                onSave={handleSave}
            />

            <Snackbar
                open={!!snackbar} autoHideDuration={4500} onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {snackbar ? (
                    <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ fontFamily }}>
                        {snackbar.message}
                    </Alert>
                ) : null}
            </Snackbar>
        </Box>
    );
};

// --- Create / Edit dialog ---------------------------------------------------
// Big-ish dialog because plans carry ~25 editable fields. Grouped into
// "Basics" / "Pricing & cycle" / "Limits" / "Features" so it scans cleanly.
const PlanFormDialog = ({ open, plan, busy, onChange, onCancel, onSave }) => {
    if (!plan) return null;
    const isNew = !plan.id;
    const set = (key) => (e) => {
        const value = e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
        onChange({ ...plan, [key]: value });
    };
    return (
        <Dialog open={open} onClose={() => !busy && onCancel()} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                {isNew ? 'Create plan' : `Edit plan — ${plan.display_name}`}
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2.5}>
                    {/* Basics */}
                    <SectionHeader title="Basics" />
                    <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth size="small" required
                                label="Slug (unique identifier)" value={plan.name || ''}
                                onChange={set('name')} disabled={!isNew}
                                helperText={isNew ? 'lowercase, e.g. "individual_basic"' : 'Slug is immutable after create.'}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth size="small" required
                                label="Display name" value={plan.display_name || ''}
                                onChange={set('display_name')}
                                helperText="Shown to customers in the pricing UI."
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select fullWidth size="small"
                                label="Account type" value={plan.account_type || ''}
                                onChange={set('account_type')}
                            >
                                {ACCOUNT_TYPES.map((t) => (
                                    <MenuItem key={t.value || 'any'} value={t.value}>{t.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ height: '100%' }}>
                                <SwitchField label="Public" value={!!plan.is_public} onChange={set('is_public')} />
                                <SwitchField label="Trial allowed" value={!!plan.is_trial_available} onChange={set('is_trial_available')} />
                            </Stack>
                        </Grid>
                    </Grid>

                    {/* Pricing & cycle */}
                    <SectionHeader title="Pricing & cycle" />
                    <Grid container spacing={1.5}>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                fullWidth size="small" type="number" inputProps={{ step: '0.01', min: 0 }}
                                label="Price" value={plan.price ?? 0}
                                onChange={set('price')}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                fullWidth size="small"
                                label="Currency" value={plan.currency || 'usd'}
                                onChange={(e) => onChange({ ...plan, currency: e.target.value.toLowerCase() })}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                select fullWidth size="small"
                                label="Plan interval" value={plan.interval || ''}
                                onChange={set('interval')}
                                helperText="Customers pick monthly/yearly per subscription."
                            >
                                {INTERVALS.map((iv) => (
                                    <MenuItem key={iv.value || 'none'} value={iv.value}>{iv.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                fullWidth size="small" type="number" inputProps={{ min: 0 }}
                                label="Trial days" value={plan.trial_days ?? 0}
                                onChange={set('trial_days')}
                                disabled={!plan.is_trial_available}
                            />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <TextField
                                select fullWidth size="small"
                                label="Discount type" value={plan.discount_type || 'percentage'}
                                onChange={set('discount_type')}
                            >
                                {DISCOUNT_TYPES.map((d) => (
                                    <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <TextField
                                fullWidth size="small" type="number" inputProps={{ step: '0.01', min: 0 }}
                                label="Discount value" value={plan.discount_value ?? 0}
                                onChange={set('discount_value')}
                                helperText="0 = no plan-level discount."
                            />
                        </Grid>
                    </Grid>

                    {/* Limits */}
                    <SectionHeader title="Feature limits"
                                   hint='Leave blank for "not applicable", or set -1 for "unlimited".' />
                    <Grid container spacing={1.25}>
                        {NUMERIC_LIMITS.map(({ key, label }) => (
                            <Grid item xs={6} sm={4} md={3} key={key}>
                                <TextField
                                    fullWidth size="small" type="number"
                                    label={label} value={plan[key] ?? ''}
                                    onChange={(e) => onChange({ ...plan, [key]: e.target.value === '' ? null : e.target.value })}
                                />
                            </Grid>
                        ))}
                    </Grid>
                    <TextField
                        fullWidth size="small"
                        label="Allowed LLMs (free text)" value={plan.allowed_llms || ''}
                        onChange={set('allowed_llms')}
                        helperText='e.g. "Google Gemini 2.0/ChatGPT-4o"'
                    />

                    {/* Features */}
                    <SectionHeader title="Feature flags" />
                    <Grid container spacing={1}>
                        {BOOL_FEATURES.map(({ key, label }) => (
                            <Grid item xs={6} sm={4} md={3} key={key}>
                                <SwitchField label={label} value={!!plan[key]} onChange={set(key)} />
                            </Grid>
                        ))}
                    </Grid>

                    {/* Outbound pricing (informational; surfaced in usage settle) */}
                    <SectionHeader title="Outbound pricing (pass-through)" />
                    <Grid container spacing={1.25}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth size="small" type="number" inputProps={{ step: '0.01' }}
                                label="WhatsApp retrieval $" value={plan.whatsapp_retrieval_price ?? ''}
                                onChange={(e) => onChange({ ...plan, whatsapp_retrieval_price: e.target.value === '' ? null : e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth size="small" type="number" inputProps={{ step: '0.01' }}
                                label="WhatsApp outbound $" value={plan.whatsapp_outbound_price ?? ''}
                                onChange={(e) => onChange({ ...plan, whatsapp_outbound_price: e.target.value === '' ? null : e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth size="small" type="number" inputProps={{ step: '0.01' }}
                                label="SMS outbound $" value={plan.sms_outbound_price ?? ''}
                                onChange={(e) => onChange({ ...plan, sms_outbound_price: e.target.value === '' ? null : e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={busy}
                        sx={{ fontFamily, textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button onClick={onSave} variant="contained" disabled={busy}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 700 }}>
                    {busy ? <CircularProgress size={18} /> : (isNew ? 'Create' : 'Save changes')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const SectionHeader = ({ title, hint }) => (
    <Box>
        <Divider sx={{ mb: 0.75 }} />
        <Typography sx={{
            fontFamily, fontSize: '0.72rem', fontWeight: 800, letterSpacing: 0.6,
            textTransform: 'uppercase', color: '#475569',
        }}>
            {title}
        </Typography>
        {hint && (
            <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#94a3b8', mt: 0.25 }}>
                {hint}
            </Typography>
        )}
    </Box>
);

const SwitchField = ({ label, value, onChange }) => (
    <Stack direction="row" spacing={0.5} alignItems="center">
        <Switch checked={value} onChange={onChange} size="small" />
        <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#475569' }}>{label}</Typography>
    </Stack>
);

export default Plans;
